import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getApiUrl } from "../config/api";
import { getUserSettings, updateMaterialRadius } from "../services/settingsService";

const SettingsContext = createContext();

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({
    generalSettings: {
      enableNotifications: true,
      showDealerLocations: true,
      enableDarkMode: false,
    },
    soundSettings: {
      playQuoteSound: true,
      playNotificationSound: true,
      volume: 50,
      soundType: "square", // "sine", "square", "sawtooth", "triangle"
    },
    deliveryLocations: [],
    materialRadiusSettings: {},
    defaultRadius: 50,
  });

  const [isLoaded, setIsLoaded] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    // First, load from localStorage
    try {
      const localSettings = localStorage.getItem("userSettings");
      if (localSettings) {
        const parsed = JSON.parse(localSettings);
        setSettings((prev) => ({
          ...prev,
          ...parsed,
        }));
      }
    } catch (err) {
      console.error("Failed to load settings from localStorage:", err);
    }

    // Then try backend - use userId from localStorage
    try {
      const userId = localStorage.getItem("userId");
      
      // Load general settings (if this endpoint exists separately)
      // Otherwise remove this block if only user settings endpoint exists
      
      // Load material radius settings from the correct endpoint
      if (userId && userId !== "default-user") {
        const userSettings = await getUserSettings(userId);
        setSettings((prev) => ({
          ...prev,
          materialRadiusSettings: userSettings.materialRadiusSettings || {},
          defaultRadius: userSettings.defaultRadius || 50,
        }));
        
        // Update localStorage with backend data
        localStorage.setItem("userSettings", JSON.stringify({
          ...JSON.parse(localStorage.getItem("userSettings") || "{}"),
          materialRadiusSettings: userSettings.materialRadiusSettings || {},
          defaultRadius: userSettings.defaultRadius || 50,
        }));
      }
    } catch (err) {
      console.error("Failed to load settings from backend:", err);
    } finally {
      setIsLoaded(true);
    }
  };

  const updateSettings = useCallback((newSettings) => {
    setSettings(newSettings);
  }, []);

  // Update material radius and persist to backend
  const updateMaterialRadiusSetting = useCallback(async (materialCategory, radius) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId || userId === "default-user") {
        console.warn("No valid user ID, cannot update material radius");
        return;
      }

      // Optimistically update local state
      setSettings((prev) => ({
        ...prev,
        materialRadiusSettings: {
          ...prev.materialRadiusSettings,
          [materialCategory]: radius,
        },
      }));

      // Persist to backend
      await updateMaterialRadius(userId, materialCategory, radius);
      console.log(`Updated ${materialCategory} radius to ${radius} miles`);
    } catch (err) {
      console.error("Failed to update material radius:", err);
      // Could add error handling/rollback here
    }
  }, []);

  // Get radius for a specific material (returns default if not set)
  const getMaterialRadiusSetting = useCallback((materialCategory) => {
    if (!settings || !settings.materialRadiusSettings) {
      return settings?.defaultRadius || 50;
    }
    return settings.materialRadiusSettings[materialCategory] || settings.defaultRadius;
  }, [settings]);

  // Play notification sound
  const playNotificationSound = useCallback((force = false) => {
    if (!force && !settings.soundSettings.playNotificationSound) return;
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = settings.soundSettings.soundType || "square";
      
      const volume = settings.soundSettings.volume / 100;
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (err) {
      console.error("Failed to play notification sound:", err);
    }
  }, [settings.soundSettings]);

  // Play quote received sound
  const playQuoteSound = useCallback((force = false) => {
    if (!force && !settings.soundSettings.playQuoteSound) return;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create a pleasant two-tone sound
      const playTone = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = settings.soundSettings.soundType || "square";
        
        const volume = settings.soundSettings.volume / 100;
        gainNode.gain.setValueAtTime(volume, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      // Play two tones - lower then higher with short gap
      playTone(600, audioContext.currentTime, 0.15);
      playTone(800, audioContext.currentTime, 0.2);
    } catch (err) {
      console.error("Failed to play quote sound:", err);
    }
  }, [settings.soundSettings]);

  const value = {
    settings,
    updateSettings,
    updateMaterialRadiusSetting,
    getMaterialRadiusSetting,
    isLoaded,
    playNotificationSound,
    playQuoteSound,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}
